// Sound files used by a map's ambient_generic entities: from the map's pakfile, from game files
// cached with the map, or from the player's own game files. Decoded later on the main thread.

import { unzipSync } from 'fflate';
import type { MapEntity } from './entities';
import type { GameFiles } from '../vpk';

/** "sound/..." key for an ambient_generic "message" (strips Source's sound prefix characters). */
export function soundKey(message: string): string | null {
  const m = message
    .trim()
    .replace(/\\/g, '/')
    .replace(/^[*#@<>^)}$!?&~`+%(]+/, '')
    .toLowerCase();
  if (!/\.(wav|mp3|ogg)$/.test(m)) return null; // soundscript names (no extension) aren't files
  return 'sound/' + m.replace(/^sound\//, '');
}

export async function collectMapSounds(
  entities: MapEntity[],
  pak: Uint8Array,
  extras: Map<string, Uint8Array>,
  game: GameFiles | null,
  fetched: Map<string, Uint8Array>,
): Promise<[string, Uint8Array][]> {
  const wanted = new Set<string>();
  for (const e of entities) {
    if (e.classname !== 'ambient_generic') continue;
    const k = soundKey(e.keys.message ?? '');
    if (k) wanted.add(k);
  }
  if (!wanted.size) return [];
  let packed: Record<string, Uint8Array> = {};
  if (pak.length >= 22) {
    try {
      packed = unzipSync(pak, { filter: (f) => wanted.has(f.name.toLowerCase().replace(/\\/g, '/')) });
    } catch {
      packed = {};
    }
  }
  const byKey = new Map(Object.entries(packed).map(([k, v]) => [k.toLowerCase().replace(/\\/g, '/'), v]));
  const out: [string, Uint8Array][] = [];
  for (const k of wanted) {
    let data = byKey.get(k) ?? extras.get(k) ?? null;
    if (!data && game) {
      data = await game.read(k);
      if (data) fetched.set(k, data);
    }
    if (data) out.push([k, data.slice()]);
  }
  return out;
}
